import type {
  Bedrijfsrelatie,
  Contactkanaal,
  Contactrichting,
  CvVerwerkingsstatus,
  Dienstverband,
  Eissoort,
  Gebruikersrol,
  Gebruikersstatus,
  Kandidaatstatus,
  LopendeFase,
  Matchbeoordeling,
  Opleidingsniveau,
  Rijbewijs,
  Taalniveau,
  Vaardigheidsniveau,
  Vacaturestatus,
  Voordrachtfase,
  Voordrachtuitkomst,
} from '../api/typen.ts';

export interface Optie<T extends string> {
  waarde: T;
  label: string;
}

/**
 * Zet een tabel met labels om naar opties voor een keuzelijst, in de volgorde van de tabel.
 * @param labels De labels per waarde.
 * @returns De opties.
 */
export function opties<T extends string>(labels: Record<T, string>): Optie<T>[] {
  return (Object.entries(labels) as [T, string][]).map(([waarde, label]) => ({ waarde, label }));
}

/**
 * Geeft het label bij een waarde, of de waarde zelf als er geen label is.
 * @param labels De labels per waarde.
 * @param waarde De waarde, of leeg.
 * @returns Het label.
 */
export function label<T extends string>(labels: Record<T, string>, waarde: T | null | undefined): string {
  return waarde ? labels[waarde] : '';
}

export const kandidaatstatusLabels: Record<Kandidaatstatus, string> = {
  nieuw: 'Nieuw',
  actief: 'Actief',
  geplaatst: 'Geplaatst',
  inactief: 'Inactief',
};

export const vacaturestatusLabels: Record<Vacaturestatus, string> = {
  concept: 'Concept',
  open: 'Open',
  gepauzeerd: 'Gepauzeerd',
  vervuld: 'Vervuld',
  gesloten: 'Gesloten',
};

export const bedrijfsrelatieLabels: Record<Bedrijfsrelatie, string> = {
  prospect: 'Prospect',
  klant: 'Klant',
  oud_klant: 'Oud-klant',
};

export const dienstverbandLabels: Record<Dienstverband, string> = {
  vast: 'Vast',
  tijdelijk: 'Tijdelijk',
  uitzenden: 'Uitzenden',
  detachering: 'Detachering',
  freelance: 'Freelance',
  stage: 'Stage',
};

export const rijbewijsLabels: Record<Rijbewijs, string> = {
  AM: 'AM',
  A1: 'A1',
  A2: 'A2',
  A: 'A',
  B: 'B',
  BE: 'BE',
  C1: 'C1',
  C1E: 'C1E',
  C: 'C',
  CE: 'CE',
  D1: 'D1',
  D1E: 'D1E',
  D: 'D',
  DE: 'DE',
  T: 'T',
};

export const opleidingsniveauLabels: Record<Opleidingsniveau, string> = {
  basisonderwijs: 'Basisonderwijs',
  vmbo: 'Vmbo',
  havo: 'Havo',
  vwo: 'Vwo',
  mbo_1: 'Mbo 1',
  mbo_2: 'Mbo 2',
  mbo_3: 'Mbo 3',
  mbo_4: 'Mbo 4',
  hbo_associate: 'Hbo associate degree',
  hbo_bachelor: 'Hbo bachelor',
  hbo_master: 'Hbo master',
  wo_bachelor: 'Wo bachelor',
  wo_master: 'Wo master',
  doctoraat: 'Doctoraat',
};

export const vaardigheidsniveauLabels: Record<Vaardigheidsniveau, string> = {
  basis: 'Basis',
  gevorderd: 'Gevorderd',
  expert: 'Expert',
};

export const taalniveauLabels: Record<Taalniveau, string> = {
  A1: 'A1',
  A2: 'A2',
  B1: 'B1',
  B2: 'B2',
  C1: 'C1',
  C2: 'C2',
  moedertaal: 'Moedertaal',
};

export const voordrachtfaseLabels: Record<Voordrachtfase, string> = {
  voorbereiding: 'Voorbereiding',
  voorgesteld: 'Voorgesteld',
  gesprek: 'Gesprek',
  aanbod: 'Aanbod',
  afgerond: 'Afgerond',
};

export const lopendeFaseLabels: Record<LopendeFase, string> = {
  voorbereiding: 'Voorbereiding',
  voorgesteld: 'Voorgesteld',
  gesprek: 'Gesprek',
  aanbod: 'Aanbod',
};

export const voordrachtuitkomstLabels: Record<Voordrachtuitkomst, string> = {
  lopend: 'Lopend',
  geplaatst: 'Geplaatst',
  afgewezen: 'Afgewezen',
  teruggetrokken: 'Teruggetrokken',
};

export const contactkanaalLabels: Record<Contactkanaal, string> = {
  telefoon: 'Telefoon',
  email: 'E-mail',
  gesprek: 'Gesprek',
  bericht: 'Bericht',
};

export const contactrichtingLabels: Record<Contactrichting, string> = {
  inkomend: 'Inkomend',
  uitgaand: 'Uitgaand',
};

export const matchbeoordelingLabels: Record<Matchbeoordeling, string> = {
  onbeoordeeld: 'Onbeoordeeld',
  goed: 'Goed',
  twijfel: 'Twijfel',
  afgewezen: 'Afgewezen',
};

export const cvVerwerkingsstatusLabels: Record<CvVerwerkingsstatus, string> = {
  wachtrij: 'In wachtrij',
  bezig: 'Wordt verwerkt',
  verwerkt: 'Verwerkt',
  mislukt: 'Mislukt',
};

export const gebruikersrolLabels: Record<Gebruikersrol, string> = {
  recruiter: 'Recruiter',
  beheerder: 'Beheerder',
};

export const gebruikersstatusLabels: Record<Gebruikersstatus, string> = {
  in_afwachting: 'Wacht op goedkeuring',
  actief: 'Actief',
  gedeactiveerd: 'Gedeactiveerd',
};

export const eissoortLabels: Record<Eissoort, string> = {
  ervaring: 'Ervaring',
  vaardigheid: 'Vaardigheid',
  certificaat: 'Certificaat',
  opleiding: 'Opleiding',
  taal: 'Taal',
  rijbewijs: 'Rijbewijs',
};

export const taalLabels: Record<string, string> = {
  nl: 'Nederlands',
  en: 'Engels',
  de: 'Duits',
  fr: 'Frans',
  pl: 'Pools',
  ro: 'Roemeens',
  es: 'Spaans',
  it: 'Italiaans',
  pt: 'Portugees',
  tr: 'Turks',
  ar: 'Arabisch',
  uk: 'Oekraïens',
  bg: 'Bulgaars',
  hu: 'Hongaars',
};

/**
 * Geeft de naam van een taal bij een taalcode.
 * @param taalcode De code van twee letters, zoals `nl`.
 * @returns De naam, of de code in hoofdletters als de taal onbekend is.
 */
export function taalnaam(taalcode: string): string {
  return taalLabels[taalcode] ?? taalcode.toUpperCase();
}

/**
 * De foutmeldingen bij de codes die de API bij het aanmelden teruggeeft.
 */
export const aanmeldfoutLabels: Record<string, string> = {
  onbekende_provider: 'Deze aanmeldmethode bestaat niet of staat uit.',
  ongeldige_aanvraag: 'De aanmelding is verlopen of ongeldig. Probeer het opnieuw.',
  ongeldige_terugkeer_url: 'Het terugkeeradres is niet toegestaan.',
  uitnodiging_ongeldig: 'De uitnodiging bestaat niet, is verlopen of is al gebruikt.',
  ongeldig_token: 'Het antwoord van de aanmeldprovider kon niet worden gecontroleerd.',
  sessie_vereist: 'Meld je eerst aan voordat je een aanmeldmethode koppelt.',
  geen_account: 'Er is geen account voor deze identiteit. Vraag een beheerder om een uitnodiging.',
  account_gedeactiveerd: 'Dit account is gedeactiveerd.',
  domein_niet_toegestaan: 'Dit e-mailadres is niet geverifieerd of valt buiten de toegestane domeinen.',
  herauthenticatie_nodig: 'Meld je opnieuw aan voordat je een aanmeldmethode ontkoppelt.',
  niet_gekoppeld: 'Deze aanmeldmethode is niet gekoppeld.',
  email_in_gebruik: 'Er bestaat al een account met dit e-mailadres.',
  identiteit_al_gekoppeld: 'Deze identiteit is al aan een ander account gekoppeld.',
  provider_al_gekoppeld: 'Deze aanmeldmethode is al gekoppeld aan je account.',
  laatste_inlogmethode: 'Je kunt je laatste aanmeldmethode niet ontkoppelen.',
  geen_email: 'De aanmeldprovider gaf geen e-mailadres door.',
  provider_fout: 'De aanmeldprovider weigerde de aanmelding.',
  provider_onbereikbaar: 'De aanmeldprovider is niet bereikbaar.',
};
